import { useEffect, useMemo, useState } from "react";
import { Pencil, Eye, EyeOff, Save, KeyRound, X } from "lucide-react";
import api, { errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const OFFICE_SUB_ROLES = [
  { v: "telecaller", l: "Telecaller" },
  { v: "field", l: "Field Staff" },
  { v: "district_manager", l: "District Manager" },
];

const STATUS_OPTIONS = [
  { v: "active", l: "Active" },
  { v: "suspended", l: "Suspended" },
  { v: "terminated", l: "Terminated" },
];

/**
 * Full-edit modal for admin — collapses profile fields, monthly target and
 * password reset into ONE screen. Each section calls its own idempotent
 * endpoint so partial edits still succeed even if another section fails.
 */
export default function AdminEditUserModal({ staff, banks = [], districts = [], open, onClose, onSaved }) {
  const isBank = staff?.role === "bank_staff";
  const isOffice = staff?.role === "office_staff";
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [f, setF] = useState({});
  const currentMonth = useMemo(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  }, []);

  useEffect(() => {
    if (!open || !staff) return;
    setF({
      name: staff.name || "",
      phone: staff.phone || "",
      email: staff.email || "",
      district: staff.district || staff.managed_district || "",
      sub_role: staff.sub_role || "telecaller",
      bank_id: staff.bank_id || "",
      monthly_salary: staff.monthly_salary || "",
      target_cases: staff.target?.target_cases || "",
      target_amount_lakhs: staff.target?.target_amount_lakhs || "",
      target_incentive: staff.target?.incentive || "",
      status: staff.status || "active",
      status_reason: "",
      suspend_days: 7,
      new_password: "",
    });
    setShowPw(false);
  }, [open, staff]);

  if (!staff) return null;

  const save = async () => {
    if (!f.name?.trim()) return toast.error("Name is required");
    if (!/^\d{10}$/.test(f.phone || "")) return toast.error("Phone must be exactly 10 digits");
    setSaving(true);
    const errors = [];
    try {
      // 1. Profile PUT — name, phone, email, district, sub_role/bank_id, salary
      const profilePayload = {
        name: f.name.trim(),
        phone: f.phone,
        email: (f.email || "").trim(),
        district: f.district || undefined,
      };
      if (isBank) profilePayload.bank_id = f.bank_id || undefined;
      if (isOffice) {
        profilePayload.sub_role = f.sub_role || undefined;
        if (f.monthly_salary !== "" && f.monthly_salary !== null) {
          profilePayload.monthly_salary = Number(f.monthly_salary);
        }
      }
      try {
        await api.put(`/admin/users/${staff.id}`, profilePayload);
      } catch (e) { errors.push(`Profile: ${errorMessage(e)}`); }

      // 2. Target — office staff only
      if (isOffice && (f.target_cases || f.target_amount_lakhs)) {
        try {
          await api.post("/admin/targets", {
            staff_id: staff.id,
            month: currentMonth.month,
            year: currentMonth.year,
            target_cases: Number(f.target_cases || 0),
            target_amount_lakhs: Number(f.target_amount_lakhs || 0),
            incentive: Number(f.target_incentive || 0),
          });
        } catch (e) { errors.push(`Target: ${errorMessage(e)}`); }
      }

      // 3. Status
      const initialStatus = staff.status || "active";
      if (f.status && f.status !== initialStatus) {
        if (f.status !== "active" && !f.status_reason?.trim()) {
          errors.push("Status change: reason required");
        } else {
          const endpoint = isBank ? "/admin/bank-staff" : "/admin/office-staff";
          const body = { status: f.status, reason: (f.status_reason || "").trim() };
          if (f.status === "suspended") body.days = Number(f.suspend_days || 7);
          try {
            await api.post(`${endpoint}/${staff.id}/status`, body);
          } catch (e) { errors.push(`Status: ${errorMessage(e)}`); }
        }
      }

      // 4. Password — optional
      if (f.new_password && f.new_password.length >= 4) {
        try {
          await api.post(`/admin/users/${staff.id}/reset-password`, { new_password: f.new_password });
        } catch (e) { errors.push(`Password: ${errorMessage(e)}`); }
      } else if (f.new_password && f.new_password.length > 0 && f.new_password.length < 4) {
        errors.push("Password must be at least 4 characters");
      }

      if (errors.length === 0) {
        toast.success(`${staff.name} updated`);
        onSaved && onSaved();
      } else {
        errors.forEach((m) => toast.error(m));
        if (errors.length < 4) onSaved && onSaved();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="admin-edit-user-modal"
        className="bg-slate-900 border-slate-800 text-white max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-400">
            <Pencil className="w-4 h-4"/> Admin Edit · {staff.name}
            <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-slate-500">
              {isBank ? "Bank Staff" : isOffice ? "Office Staff" : (staff.role || "")}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 pt-2">
          {/* --- Section: Profile --- */}
          <section className="space-y-3">
            <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold">Profile</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-400 text-xs uppercase tracking-widest">Name</Label>
                <Input data-testid="ae-name" value={f.name || ""} onChange={(e)=>setF({...f, name: e.target.value})} className="bg-slate-950 border-slate-700 mt-2"/>
              </div>
              <div>
                <Label className="text-slate-400 text-xs uppercase tracking-widest">Phone / Login ID</Label>
                <Input data-testid="ae-phone" maxLength={10} value={f.phone || ""} onChange={(e)=>setF({...f, phone: e.target.value.replace(/\D/g,"")})} className="bg-slate-950 border-slate-700 mt-2 font-mono-alt"/>
              </div>
              <div>
                <Label className="text-slate-400 text-xs uppercase tracking-widest">Email</Label>
                <Input data-testid="ae-email" type="email" value={f.email || ""} onChange={(e)=>setF({...f, email: e.target.value})} className="bg-slate-950 border-slate-700 mt-2"/>
              </div>
              <div>
                <Label className="text-slate-400 text-xs uppercase tracking-widest">District</Label>
                <Select value={f.district || ""} onValueChange={(v)=>setF({...f, district: v})}>
                  <SelectTrigger data-testid="ae-district" className="bg-slate-950 border-slate-700 mt-2 w-full"><SelectValue placeholder="Select district"/></SelectTrigger>
                  <SelectContent className="bg-slate-900 text-white border-slate-700 max-h-64 min-w-[--radix-select-trigger-width]">
                    {districts.map((d)=>(<SelectItem key={d.id || d.name} value={d.name}>{d.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              {isBank && (
                <div className="sm:col-span-2">
                  <Label className="text-slate-400 text-xs uppercase tracking-widest">Bank</Label>
                  <Select value={f.bank_id || ""} onValueChange={(v)=>setF({...f, bank_id: v})}>
                    <SelectTrigger data-testid="ae-bank" className="bg-slate-950 border-slate-700 mt-2 w-full"><SelectValue placeholder="Select bank"/></SelectTrigger>
                    <SelectContent className="bg-slate-900 text-white border-slate-700 max-h-64 min-w-[--radix-select-trigger-width]">
                      {banks.map((b)=>(<SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {isOffice && (
                <>
                  <div>
                    <Label className="text-slate-400 text-xs uppercase tracking-widest">Role</Label>
                    <Select value={f.sub_role || ""} onValueChange={(v)=>setF({...f, sub_role: v})}>
                      <SelectTrigger data-testid="ae-sub-role" className="bg-slate-950 border-slate-700 mt-2 w-full"><SelectValue/></SelectTrigger>
                      <SelectContent className="bg-slate-900 text-white border-slate-700">
                        {OFFICE_SUB_ROLES.map((r)=>(<SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-400 text-xs uppercase tracking-widest">Monthly Salary (₹)</Label>
                    <Input data-testid="ae-salary" type="number" value={f.monthly_salary || ""} onChange={(e)=>setF({...f, monthly_salary: e.target.value.replace(/\D/g,"")})} className="bg-slate-950 border-slate-700 mt-2"/>
                  </div>
                </>
              )}
            </div>
          </section>

          {/* --- Section: Target (office staff only) --- */}
          {isOffice && (
            <section className="space-y-3 pt-2 border-t border-slate-800">
              <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold">Monthly Target · {currentMonth.year}-{String(currentMonth.month).padStart(2,"0")}</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-slate-400 text-xs uppercase tracking-widest">Target Cases</Label>
                  <Input data-testid="ae-target-cases" type="number" placeholder="e.g. 20" value={f.target_cases || ""} onChange={(e)=>setF({...f, target_cases: e.target.value.replace(/\D/g,"")})} className="bg-slate-950 border-slate-700 mt-2"/>
                </div>
                <div>
                  <Label className="text-slate-400 text-xs uppercase tracking-widest">Target ₹ (Lakhs)</Label>
                  <Input data-testid="ae-target-amount" type="number" placeholder="e.g. 200" value={f.target_amount_lakhs || ""} onChange={(e)=>setF({...f, target_amount_lakhs: e.target.value.replace(/\D/g,"")})} className="bg-slate-950 border-slate-700 mt-2"/>
                </div>
                <div>
                  <Label className="text-slate-400 text-xs uppercase tracking-widest">Bonus (₹)</Label>
                  <Input data-testid="ae-target-incentive" type="number" placeholder="e.g. 5000" value={f.target_incentive || ""} onChange={(e)=>setF({...f, target_incentive: e.target.value.replace(/\D/g,"")})} className="bg-slate-950 border-slate-700 mt-2"/>
                </div>
              </div>
            </section>
          )}

          {/* --- Section: Status --- */}
          <section className="space-y-3 pt-2 border-t border-slate-800">
            <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold">Account Status</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {STATUS_OPTIONS.map((o) => (
                <button
                  key={o.v}
                  data-testid={`ae-status-${o.v}`}
                  onClick={() => setF({...f, status: o.v})}
                  className={`px-3 py-2 rounded-lg text-sm border font-bold uppercase tracking-widest transition-colors ${
                    f.status === o.v
                      ? o.v === "active" ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/50"
                        : o.v === "suspended" ? "bg-amber-500/20 text-amber-300 border-amber-500/50"
                        : "bg-red-500/20 text-red-300 border-red-500/50"
                      : "bg-slate-950 text-slate-400 border-slate-800 hover:border-slate-700"
                  }`}>
                  {o.l}
                </button>
              ))}
            </div>
            {f.status === "suspended" && (
              <div>
                <Label className="text-slate-400 text-xs uppercase tracking-widest">Suspend Duration (days)</Label>
                <Input data-testid="ae-suspend-days" type="number" value={f.suspend_days || 7} onChange={(e)=>setF({...f, suspend_days: e.target.value.replace(/\D/g,"")})} className="bg-slate-950 border-slate-700 mt-2 max-w-[160px]"/>
              </div>
            )}
            {f.status && f.status !== (staff.status || "active") && f.status !== "active" && (
              <div>
                <Label className="text-slate-400 text-xs uppercase tracking-widest">Reason <span className="text-red-400">*</span></Label>
                <Textarea data-testid="ae-status-reason" value={f.status_reason || ""} onChange={(e)=>setF({...f, status_reason: e.target.value})} placeholder="Why is this staff being changed?" className="mt-2 bg-slate-950 border-slate-700 min-h-[70px]"/>
              </div>
            )}
          </section>

          {/* --- Section: Password (optional) --- */}
          <section className="space-y-3 pt-2 border-t border-slate-800">
            <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold flex items-center gap-2">
              <KeyRound className="w-3.5 h-3.5"/> Reset Password <span className="text-slate-500 normal-case tracking-normal">(optional — leave blank to keep current)</span>
            </div>
            <div className="relative max-w-md">
              <Input
                data-testid="ae-password"
                type={showPw ? "text" : "password"}
                value={f.new_password || ""}
                onChange={(e)=>setF({...f, new_password: e.target.value})}
                placeholder="Set new password"
                className="bg-slate-950 border-slate-700 pr-10"/>
              <button
                type="button"
                onClick={()=>setShowPw((v)=>!v)}
                data-testid="ae-password-toggle"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-amber-400">
                {showPw ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
              </button>
            </div>
          </section>

          <div className="flex gap-2 pt-2 border-t border-slate-800">
            <Button
              data-testid="ae-cancel"
              variant="ghost"
              onClick={onClose}
              className="text-slate-400 hover:text-white">
              <X className="w-4 h-4 mr-1"/> Cancel
            </Button>
            <Button
              data-testid="ae-save"
              onClick={save}
              disabled={saving}
              className="ml-auto bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full">
              <Save className="w-4 h-4 mr-1"/> {saving ? "Saving…" : "Save & Update"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
